<template>
  <div ref="containerRef" class="ofs-transactions-new">
    <template v-if="transactions.length">
      <TransitionRoot as="div" class="ofs-transactions-new__list">
        <TransitionChild
          v-for="(transaction, index) in transactions"
          :key="index"
          as="template"
          enter="transition-transform ease-linear duration-300"
          enter-from="scale-0 opacity-0"
          enter-to="scale-100 opacity-100"
        >
          <OFSTransactionsNewItem
            :dates="transactionDatesMap"
            :transaction-settings
            :transaction
            @delete="handleDelete(index)"
            @update:date="handleUpdateDate"
          />
        </TransitionChild>
      </TransitionRoot>
    </template>
    <UILayoutNoData v-else hide-question-icon inline class="ofs-transactions-new__empty">
      <template #text-description>No transactions to add</template>
    </UILayoutNoData>
  </div>
</template>

<script setup lang="ts">
import { nextTick, useTemplateRef } from 'vue'
import { TransitionChild, TransitionRoot } from '@headlessui/vue'

import { TransactionSettings } from '@types'
import { OrganizeFamilyStructureTransaction } from './utils/types'

import OFSTransactionsNewItem from './OrganizeFamilyStructureTransactionsNewItem.vue'
import { UILayoutNoData } from '@ui/core'

type Props = {
  transactions: OrganizeFamilyStructureTransaction[]
  transactionSettings?: TransactionSettings
}

type Emits = {
  delete: [index: number]
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const transactionDatesMap = defineModel<Map<string, string>>('dates', { required: true })

defineExpose({
  scrollToBottom: () => {
    nextTick(() => {
      containerRef.value?.scrollTo?.({
        top: containerRef.value.scrollHeight,
        behavior: 'smooth',
      })
    })
  },
})

const containerRef = useTemplateRef('containerRef')

const handleUpdateDate = (date: string, accountId: string, assetId: string) => {
  transactionDatesMap.value.set(`${accountId}-${assetId}`, date)
}

const handleDelete = (index: number) => {
  emit('delete', index)
}
</script>

<style>
.ofs-transactions-new {
  @apply h-full;
  @apply pr-2;
  @apply overflow-y-auto;

  &__list {
    @apply sm:min-w-[640px];
    @apply flex flex-col gap-2;
  }

  &__empty {
    @apply h-full;
    @apply !items-center !justify-center;

    p {
      @apply text-sm;
    }
  }
}
</style>
